import { useCallback, useRef, useState } from "react";
import customAxios from "../utils/customAxios";
import { Toast } from "react-native-toast-notifications";
import i18n from "../locales/i18n";

interface ProductInfo {
  id_product: number;
  name: string;
  price_sell: number;
  photo: string;
}

export interface Order {
  id_order: number;
  client_fullname: string;
  client_phone: string;
  product_id: number;
  quantity: number;
  store_id: number;
  location: string;
  confirmed: boolean;
  createdAt: string;
  updatedAt: string;
  Store?: { id_store: number; name: string };
  Product?: ProductInfo;
}

// Cache product info to avoid re-fetching
const productCache = new Map<number, ProductInfo>();

async function fetchProductById(id: number): Promise<ProductInfo | null> {
  if (productCache.has(id)) return productCache.get(id)!;
  try {
    const res = await customAxios.get(`/stock/${id}`, {
      withCredentials: true,
    });
    const p = res.data;
    const info: ProductInfo = {
      id_product: p.id_product,
      name: p.name,
      price_sell: Number(p.price_sell),
      photo: p.photo ?? "",
    };
    productCache.set(id, info);
    return info;
  } catch {
    return null;
  }
}

async function enrichOrdersWithProducts(orders: Order[]): Promise<Order[]> {
  const idsToFetch = [
    ...new Set(
      orders.filter((o) => !o.Product && o.product_id).map((o) => o.product_id),
    ),
  ];
  // Fetch all missing products in parallel
  const results = await Promise.all(idsToFetch.map(fetchProductById));
  const productMap = new Map<number, ProductInfo>();
  idsToFetch.forEach((id, i) => {
    if (results[i]) productMap.set(id, results[i]);
  });

  return orders.map((o) => {
    if (o.Product) return o;
    const product = productMap.get(o.product_id);
    return product ? { ...o, Product: product } : o;
  });
}

export const useOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchOrders = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    try {
      const res = await customAxios.get("/orders", {
        withCredentials: true,
        timeout: 10000,
      });
      const data: Order[] = Array.isArray(res.data) ? res.data : [];
      const enriched = await enrichOrdersWithProducts(data);
      setOrders(enriched);
      return enriched;
    } catch (error: any) {
      if (error.response?.status === 404) {
        setOrders([]);
        return [];
      }
      Toast.show(i18n.t("orderError"), { type: "danger" });
      return [];
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const confirmOrder = useCallback(async (orderId: number) => {
    try {
      await customAxios.patch(
        `/orders/${orderId}`,
        { confirmed: true },
        { withCredentials: true },
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.id_order === orderId ? { ...o, confirmed: true } : o,
        ),
      );
      Toast.show(i18n.t("orderConfirmedSuccess"), { type: "success" });
    } catch {
      Toast.show(i18n.t("orderError"), { type: "danger" });
    }
  }, []);

  const cancelOrder = useCallback(async (orderId: number) => {
    try {
      await customAxios.patch(
        `/orders/${orderId}`,
        { confirmed: false },
        { withCredentials: true },
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.id_order === orderId ? { ...o, confirmed: false } : o,
        ),
      );
      Toast.show(i18n.t("orderCancelledSuccess"), { type: "success" });
    } catch {
      Toast.show(i18n.t("orderError"), { type: "danger" });
    }
  }, []);

  const deleteOrder = useCallback(async (orderId: number) => {
    try {
      await customAxios.delete(`/orders/${orderId}`, { withCredentials: true });
      setOrders((prev) => prev.filter((o) => o.id_order !== orderId));
      Toast.show(i18n.t("orderDeletedSuccess"), { type: "success" });
    } catch {
      Toast.show(i18n.t("orderError"), { type: "danger" });
    }
  }, []);

  return {
    orders,
    isLoading,
    isRefreshing,
    fetchOrders,
    confirmOrder,
    cancelOrder,
    deleteOrder,
  };
};
